import React from "react";
import { Alert, Box, TextField } from "@mui/material";
import { observer } from "mobx-react";
import { useRootStore } from "../../../../store/contexts";
import { useFormRecord } from "../../../../common/controls/forms/FormRecordContext";

/**
 * Campo Importe del comprobante: input decimal + cartel en vivo que traduce el
 * importe a cantidad de usos según el precio vigente del tipo seleccionado
 * (PaymentReceiptType.price, cacheado). La cantidad de usos posibles la calcula
 * el server al crear (división entera importe/precio); acá solo se anticipa el
 * resultado y se advierte el excedente que no se aplicará.
 * El tipo seleccionado llega por FormRecordContext.values (refs simples como
 * {displayId, id}); el precio sale del cache de PaymentReceiptType.
 */
export interface PaymentReceiptAmountCustomFieldProps {
  value: any;
  onChange: (value: any) => void;
  error?: boolean;
  helperText?: string;
  label?: string;
  description?: string;
}

const formatArs = (n: number) =>
  n.toLocaleString("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 2 });

export const PaymentReceiptAmountCustomField: React.FC<PaymentReceiptAmountCustomFieldProps> =
  observer(({ value, onChange, error, helperText, label, description }) => {
    const rootStore = useRootStore();
    const { values } = useFormRecord();

    // Fallback a tipo único: si el contexto aún no trae la ref (form viejo sin
    // regenerar) o no hay tipo elegido, con un solo PaymentReceiptType alcanza.
    const selectedTypeId: string | undefined = values?.type?.id;
    const types = rootStore.referenceCacheStore.getRawData("PaymentReceiptType");
    const price: number | undefined =
      types.find((r: any) => r.id === selectedTypeId)?.price ??
      (types.length === 1 ? types[0].price : undefined);

    const amount = typeof value === "number" ? value : parseFloat(String(value ?? ""));
    const hasAmount = Number.isFinite(amount) && amount > 0;
    const hasPrice = typeof price === "number" && price > 0;
    const uses = hasAmount && hasPrice ? Math.floor(amount / price + 1e-9) : 0;
    const leftover = hasAmount && hasPrice ? Math.round((amount - uses * price) * 100) / 100 : 0;

    return (
      <Box>
        <TextField
          fullWidth
          type="number"
          label={label}
          value={value ?? ""}
          error={error}
          helperText={helperText || description}
          inputProps={{ min: 0, step: "any" }}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "") {
              onChange("");
              return;
            }
            const n = parseFloat(raw);
            onChange(Number.isNaN(n) ? "" : Math.max(0, n));
          }}
        />
        {!hasPrice ? (
          <Alert severity="info" sx={{ mt: 1, mb: 1 }}>
            Falta el tipo de comprobante para calcular la cantidad de usos que
            cubre el importe.
          </Alert>
        ) : !hasAmount || uses === 0 ? (
          <Alert severity={hasAmount ? "error" : "info"} sx={{ mt: 1, mb: 1 }}>
            El importe debe cubrir al menos un uso: el precio vigente del
            trámite es {formatArs(price!)}.
          </Alert>
        ) : leftover === 0 ? (
          <Alert severity="info" sx={{ mt: 1, mb: 1 }}>
            El importe cubre exactamente <strong>{uses}</strong>{" "}
            {uses === 1 ? "uso" : "usos"} de {formatArs(price!)}.
          </Alert>
        ) : (
          <Alert severity="warning" sx={{ mt: 1, mb: 1 }}>
            El importe cubre <strong>{uses}</strong>{" "}
            {uses === 1 ? "uso" : "usos"} de {formatArs(price!)}; sobran{" "}
            <strong>{formatArs(leftover)}</strong> que no se aplicarán a ningún
            trámite.
          </Alert>
        )}
      </Box>
    );
  });
